import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTwin } from '../context/TwinContext';
import { alertStore } from '../utils/alertStore';
import './Reports.css';

/* ── helpers ─────────────────────────────────────────────────── */

function toCSV(headers: string[], rows: (string | number)[][]): string {
  return [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
}

function download(filename: string, content: string, mime = 'text/csv') {
  const blob = new Blob([content], { type: mime });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function sensorHealth(value: number, min: number, max: number) {
  const range = max - min;
  if (range === 0) return { pct: 0, risk: 'LOW' as const, fp: 0 };
  const pct = ((value - min) / range) * 100;
  const fp  = Math.max(0, Math.min(100, pct > 80 ? (pct - 80) * 5 : pct > 60 ? (pct - 60) * 1.5 : 0));
  const risk = fp > 60 ? 'HIGH' as const : fp > 30 ? 'MEDIUM' as const : 'LOW' as const;
  return { pct: +pct.toFixed(1), risk, fp: +fp.toFixed(1) };
}

type Fmt = 'csv' | 'json';

/* ── component ──────────────────────────────────────────────── */

const Reports: React.FC = () => {
  const { getActiveTwin } = useTwin();
  const activeTwin = getActiveTwin();

  const [generating, setGenerating] = useState<string | null>(null);
  const [preview, setPreview]       = useState<{ id: string; headers: string[]; rows: (string | number)[][] } | null>(null);
  const [history, setHistory]       = useState<{ name: string; time: string; rows: number; size: string; format: Fmt; content: string }[]>([]);
  const [fmt, setFmt]               = useState<Fmt>('csv');

  const machines = useMemo(() => activeTwin?.machines || [], [activeTwin]);
  const simData  = useMemo(() => activeTwin?.simulatedMachineData || {}, [activeTwin]);

  // Subscribe to alertStore so reports always show current alerts
  const [liveAlerts, setLiveAlerts] = useState(() => alertStore.getAlerts());
  useEffect(() => {
    const unsub = alertStore.subscribe(() => setLiveAlerts(alertStore.getAlerts()));
    return () => { unsub(); };
  }, []);

  /* ── build report data from real twin ─────────────────────── */

  const mlRows = useMemo(() => {
    return machines.flatMap(m =>
      m.sensors.map(s => {
        const val = simData[m.machineId]?.[s.sensorId] ?? s.value;
        const { fp, risk } = sensorHealth(val, s.min, s.max);
        // Prefer EWMA-based failure prob from alertStore if available
        const ewmaAlert = liveAlerts.find(a => a.machineName === m.name && a.source === 'ewma');
        const realFp   = ewmaAlert ? ewmaAlert.failureProb : fp;
        const realRisk = ewmaAlert ? ewmaAlert.severity.toUpperCase() : risk;
        const rul = Math.max(0, ewmaAlert?.rul ?? Math.round(90 * (1 - realFp / 100)));
        const action = realRisk === 'CRITICAL' || realRisk === 'HIGH' ? 'Schedule Maintenance' : realRisk === 'MEDIUM' ? 'Monitor Closely' : 'Normal Operation';
        return {
          machine: m.name, sensor: s.name, type: s.type,
          value: +val.toFixed(2), unit: s.unit, min: s.min, max: s.max,
          fp: realFp, rul, risk: realRisk, action,
        };
      })
    );
  }, [machines, simData, liveAlerts]);

  const entityRows = useMemo(() => {
    return machines.flatMap(m => [
      { id: m.machineId, name: m.name, kind: 'Machine', type: m.type,
        status: m.status === 'running' ? 'ACTIVE' : m.status === 'fault' ? 'ERROR' : 'IDLE',
        value: '', unit: '', location: `${activeTwin?.company || 'Factory'} — ${m.name}` },
      ...m.sensors.map(s => {
        const val = simData[m.machineId]?.[s.sensorId] ?? s.value;
        return {
          id: s.sensorId, name: `${m.name} › ${s.name}`, kind: 'Sensor', type: s.type,
          status: val > s.max * 0.95 ? 'CRITICAL' : val > s.max * 0.8 ? 'WARNING' : 'OK',
          value: val.toFixed(2), unit: s.unit, location: m.name,
        };
      }),
    ]);
  }, [machines, simData, activeTwin]);

  const alertRows = useMemo(() => {
    // Use real EWMA alerts from alertStore — same data as Alerts page
    return liveAlerts.map(a => ({
      machine:   a.machineName,
      sensor:    a.sensorName,
      severity:  a.severity.toUpperCase(),
      value:     a.currentValue.toFixed(2),
      threshold: a.ucl2 ? a.ucl2.toFixed(2) : a.threshold.max.toString(),
      message:   a.recommendedAction,
      time:      a.timestamp,
    }));
  }, [liveAlerts]);

  /* ── report definitions ───────────────────────────────────── */

  const REPORTS = useMemo(() => [
    { id: 'ml', icon: '🤖', label: 'ML Predictions', accent: '#a78bfa',
      desc: 'Failure probability, RUL & risk level derived from live sensor readings',
      rows: mlRows.length, sensors: machines.reduce((n, m) => n + m.sensors.length, 0),
      headers: ['Machine', 'Sensor', 'Type', 'Value', 'Unit', 'Min', 'Max', 'Failure Prob (%)', 'RUL (days)', 'Risk', 'Action'],
      data: mlRows.map(r => [r.machine, r.sensor, r.type, r.value, r.unit, r.min, r.max, r.fp, r.rul, r.risk, r.action]),
    },
    { id: 'entities', icon: '📦', label: 'Entity Status', accent: '#00d4ff',
      desc: 'All machines & sensors with current values, status and location',
      rows: entityRows.length, sensors: entityRows.filter(e => e.kind === 'Sensor').length,
      headers: ['ID', 'Name', 'Kind', 'Type', 'Status', 'Value', 'Unit', 'Location'],
      data: entityRows.map(e => [e.id, e.name, e.kind, e.type, e.status, e.value, e.unit, e.location]),
    },
    { id: 'alerts', icon: '🔔', label: 'Alert Summary', accent: '#f87171',
      desc: 'Threshold breaches & anomalies from current sensor state',
      rows: alertRows.length, sensors: alertRows.filter(a => a.severity === 'CRITICAL').length,
      headers: ['Machine', 'Sensor', 'Severity', 'Value', 'Threshold', 'Message', 'Timestamp'],
      data: alertRows.map(a => [a.machine, a.sensor, a.severity, a.value, a.threshold, a.message, a.time]),
    },
    { id: 'full', icon: '📋', label: 'Full Factory Report', accent: '#34d399',
      desc: 'Combined ML + entities + alerts + workspace metadata',
      rows: mlRows.length + entityRows.length + alertRows.length + 6,
      sensors: machines.reduce((n, m) => n + m.sensors.length, 0),
      headers: [], data: [],
    },
  ], [mlRows, entityRows, alertRows, machines]);

  /* ── generate & download ──────────────────────────────────── */

  const generate = useCallback((reportId: string) => {
    setGenerating(reportId);
    setTimeout(() => {
      const ts = new Date().toISOString().slice(0, 19).replace('T', '_').replace(/:/g, '-');
      const report = REPORTS.find(r => r.id === reportId)!;
      let content = ''; let rows = 0; let filename = '';

      if (reportId === 'full') {
        if (fmt === 'json') {
          const obj = {
            workspace: { id: activeTwin?.id, company: activeTwin?.company, factoryType: activeTwin?.factoryType, healthScore: activeTwin?.healthScore, generated: new Date().toISOString() },
            mlPredictions: mlRows,
            entities: entityRows,
            alerts: alertRows,
          };
          content = JSON.stringify(obj, null, 2);
          rows = mlRows.length + entityRows.length + alertRows.length;
          filename = `full_report_${ts}.json`;
        } else {
          content = `=== WORKSPACE ===\nTwin ID,${activeTwin?.id}\nCompany,${activeTwin?.company}\nFactory Type,${activeTwin?.factoryType}\nHealth Score,${activeTwin?.healthScore}%\nMachines,${machines.length}\nGenerated,${new Date().toISOString()}\n\n`;
          content += `=== ML PREDICTIONS ===\n${toCSV(REPORTS[0].headers, REPORTS[0].data)}\n\n`;
          content += `=== ENTITIES ===\n${toCSV(REPORTS[1].headers, REPORTS[1].data)}\n\n`;
          content += `=== ALERTS ===\n${toCSV(REPORTS[2].headers, REPORTS[2].data)}\n`;
          rows = report.rows;
          filename = `full_report_${ts}.csv`;
        }
      } else {
        if (fmt === 'json') {
          content = JSON.stringify(report.data.map((row, i) =>
            Object.fromEntries(report.headers.map((h, j) => [h, row[j]]))
          ), null, 2);
          filename = `${reportId}_${ts}.json`;
        } else {
          content = toCSV(report.headers, report.data);
          filename = `${reportId}_${ts}.csv`;
        }
        rows = report.data.length;
      }

      download(filename, content, fmt === 'json' ? 'application/json' : 'text/csv');
      const size = content.length > 1024 ? `${(content.length / 1024).toFixed(1)} KB` : `${content.length} B`;
      setHistory(prev => [{ name: filename, time: new Date().toLocaleTimeString(), rows, size, format: fmt, content }, ...prev.slice(0, 9)]);
      setGenerating(null);
    }, 600);
  }, [REPORTS, fmt, activeTwin, mlRows, entityRows, alertRows, machines]);

  const togglePreview = (reportId: string) => {
    if (preview?.id === reportId) { setPreview(null); return; }
    const report = REPORTS.find(r => r.id === reportId)!;
    if (reportId === 'full') {
      setPreview({ id: 'full', headers: REPORTS[0].headers, rows: REPORTS[0].data.slice(0, 5) });
    } else {
      setPreview({ id: reportId, headers: report.headers, rows: report.data.slice(0, 8) });
    }
  };

  /* ── KPIs ─────────────────────────────────────────────────── */

  const totalSensors = machines.reduce((n, m) => n + m.sensors.length, 0);
  const critCount    = alertRows.filter(a => a.severity === 'CRITICAL').length;
  const warnCount    = alertRows.filter(a => a.severity === 'WARNING').length;
  const highRisk     = mlRows.filter(r => r.risk === 'HIGH').length;

  /* ── empty state ──────────────────────────────────────────── */

  if (!activeTwin) {
    return (
      <div className="reports-page">
        <div className="reports-empty">
          <div className="reports-empty-icon">📋</div>
          <h2>No Active Twin</h2>
          <p>Create or select a digital twin to generate reports from real data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-page">
      {/* Header */}
      <div className="reports-header">
        <div className="reports-header-left">
          <h1>📋 Reports & Export</h1>
          <span className="reports-twin-badge">
            {activeTwin.factoryType === 'cookie' ? '🍪' : '🏭'} {activeTwin.company || activeTwin.name}
          </span>
        </div>
        <div className="reports-format-toggle">
          <button className={`fmt-btn ${fmt === 'csv' ? 'active' : ''}`} onClick={() => setFmt('csv')}>CSV</button>
          <button className={`fmt-btn ${fmt === 'json' ? 'active' : ''}`} onClick={() => setFmt('json')}>JSON</button>
        </div>
      </div>

      {/* KPI bar */}
      <div className="reports-kpis">
        {[
          { label: 'Machines',  value: machines.length, icon: '⚙️', color: '#a78bfa' },
          { label: 'Sensors',   value: totalSensors,    icon: '📡', color: '#00d4ff' },
          { label: 'Health',    value: `${activeTwin.healthScore}%`, icon: '💚', color: activeTwin.healthScore >= 80 ? '#34d399' : activeTwin.healthScore >= 50 ? '#fbbf24' : '#f87171' },
          { label: 'Critical',  value: critCount,       icon: '🔴', color: '#f87171' },
          { label: 'Warnings',  value: warnCount,       icon: '🟡', color: '#fbbf24' },
          { label: 'High Risk', value: highRisk,        icon: '⚠️', color: '#f97316' },
        ].map((k, i) => (
          <div className="reports-kpi" key={i}>
            <span className="rk-icon">{k.icon}</span>
            <span className="rk-value" style={{ color: k.color }}>{k.value}</span>
            <span className="rk-label">{k.label}</span>
          </div>
        ))}
      </div>

      {/* Report cards */}
      <div className="reports-grid">
        {REPORTS.map(r => (
          <div className="report-card" key={r.id} style={{ '--accent': r.accent } as React.CSSProperties}>
            <div className="rc-accent" />
            <div className="rc-top">
              <span className="rc-icon">{r.icon}</span>
              <div className="rc-info">
                <h3>{r.label}</h3>
                <p>{r.desc}</p>
              </div>
            </div>
            <div className="rc-stats">
              <div className="rc-stat">
                <span className="rc-stat-val">{r.rows}</span>
                <span className="rc-stat-lbl">rows</span>
              </div>
              <div className="rc-stat">
                <span className="rc-stat-val">{r.id === 'alerts' ? r.sensors : r.sensors}</span>
                <span className="rc-stat-lbl">{r.id === 'alerts' ? 'critical' : 'sensors'}</span>
              </div>
              <div className="rc-stat">
                <span className="rc-stat-val fmt-badge">{fmt.toUpperCase()}</span>
                <span className="rc-stat-lbl">format</span>
              </div>
            </div>
            <div className="rc-actions">
              <button className="rc-preview-btn" onClick={() => togglePreview(r.id)}>
                {preview?.id === r.id ? '✕ Close' : '👁 Preview'}
              </button>
              <button className="rc-download-btn" disabled={generating === r.id || r.rows === 0}
                onClick={() => generate(r.id)}>
                {generating === r.id ? '⏳ Generating…' : `⬇ Download ${fmt.toUpperCase()}`}
              </button>
            </div>

            {/* Inline preview */}
            {preview?.id === r.id && (
              <div className="rc-preview">
                <div className="rc-preview-scroll">
                  <table>
                    <thead>
                      <tr>{preview.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((row, ri) => (
                        <tr key={ri}>{row.map((cell, ci) => {
                          const val = String(cell);
                          const cls = val === 'HIGH' || val === 'CRITICAL' ? 'cell-red'
                                    : val === 'MEDIUM' || val === 'WARNING' ? 'cell-yellow'
                                    : val === 'LOW' || val === 'OK' || val === 'ACTIVE' ? 'cell-green' : '';
                          return <td key={ci} className={cls}>{val}</td>;
                        })}</tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(r.data.length > 8 || r.id === 'full') && (
                  <div className="rc-preview-more">
                    Showing {Math.min(r.id === 'full' ? 5 : 8, r.data.length)} of {r.rows} rows — download for full data
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Download history */}
      {history.length > 0 && (
        <div className="reports-history">
          <h3>🕐 Download History</h3>
          <div className="rh-list">
            {history.map((h, i) => (
              <div className="rh-item" key={i}>
                <div className="rh-icon">{h.format === 'json' ? '{ }' : '📄'}</div>
                <div className="rh-info">
                  <span className="rh-name">{h.name}</span>
                  <span className="rh-meta">{h.time} · {h.rows} rows · {h.size}</span>
                </div>
                <button className="rh-redownload" onClick={() => download(h.name, h.content, h.format === 'json' ? 'application/json' : 'text/csv')}>
                  ⬇ Re-download
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
